/**
 * User Domain Seed Data
 *
 * Contains all seed data for the Account Service (24):
 * - Users
 * - User Roles
 * - User Realm Memberships
 */

import { DEFAULT_PASSWORD, seedId } from '../utils';
import { BUSINESS_IDS, REALM_IDS } from './tenants';

// Re-export DEFAULT_PASSWORD for backward compatibility
export { DEFAULT_PASSWORD };

// =============================================================================
// USER IDs
// =============================================================================

export const USER_IDS = {
  // Platform Users
  SUPER_ADMIN: seedId('user-superadmin', 1),
  PLATFORM_ADMIN: seedId('user-platadmin', 2),

  // Fiops Capital Users
  RACHEL_MARTINEZ: seedId('user-rachel', 3),
  OPS_MANAGER: seedId('user-ops-mgr', 4),
  SARAH_CHEN: seedId('user-sarah', 5),
  MIKE_JOHNSON: seedId('user-mike', 6),
  JAMES_WILSON: seedId('user-james', 7),
  EMILY_DAVIS: seedId('user-emily', 8),
  // Additional Fiops Capital Users (PR #237 User/Employee Management)
  MIKE_CHEN: seedId('user-mike-chen', 13),
  SARAH_WILLIAMS: seedId('user-sarah-williams', 14),
  DAVID_PARK: seedId('user-david-park', 15),
  EMILY_NGUYEN: seedId('user-emily-nguyen', 16),
  MARIA_RODRIGUEZ: seedId('user-maria-rodriguez', 17),
  JOHN_SMITH: seedId('user-john-smith', 18),
  ALEX_KUMAR: seedId('user-alex-kumar', 19),

  // 1Sharpe Capital Users
  PORTFOLIO_MANAGER: seedId('user-portfolio', 9),
  ANALYST: seedId('user-analyst', 10),
  SHARPE_ADMIN: seedId('user-sharpe-admin', 20),

  // Pacific Bridge Fund Users
  FUND_MANAGER: seedId('user-fundmgr', 11),
  PACIFIC_ADMIN: seedId('user-pacific-admin', 21),

  // Gulf Coast Family Office Users
  PRINCIPAL: seedId('user-principal', 12),

  // Issue #469: Invited/Deactivated/Status Variation Users
  INVITED_LOAN_OFFICER: seedId('user-invited-lo', 22),
  INVITED_PROCESSOR: seedId('user-invited-proc', 23),
  DEACTIVATED_UNDERWRITER: seedId('user-deactivated-uw', 24),
  SUSPENDED_ANALYST: seedId('user-suspended-analyst', 25),
  VIEWER_READONLY: seedId('user-viewer', 26),
  // Loan processor
  LISA_WANG: seedId('user-lisa-wang', 27),
} as const;

export type UserId = (typeof USER_IDS)[keyof typeof USER_IDS];

// =============================================================================
// USER ROLES
// =============================================================================

export const USER_ROLES = {
  // Platform-level roles
  SUPER_ADMIN: 'super_admin',
  PLATFORM_ADMIN: 'platform_admin',

  // Business-level roles
  BUSINESS_ADMIN: 'business_admin',
  OPS_MANAGER: 'ops_manager',
  UNDERWRITER: 'underwriter',
  CLOSER: 'closer',
  DRAW_INSPECTOR: 'draw_inspector',
  SERVICING: 'servicing',
  PORTFOLIO_MANAGER: 'portfolio_manager',
  ANALYST: 'analyst',
  FUND_MANAGER: 'fund_manager',
  PRINCIPAL: 'principal',

  // Standard roles (Issue #469)
  LOAN_OFFICER: 'loan_officer',
  PROCESSOR: 'processor',
  VIEWER: 'viewer',

  // Consumer-level roles
  BORROWER: 'borrower',
  PARTNER: 'partner',
} as const;

export type SeedUserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

// =============================================================================
// USER STATUS (Issue #469)
// =============================================================================

export const USER_STATUS = {
  ACTIVE: 'active',
  INVITED: 'invited',
  DEACTIVATED: 'deactivated',
  SUSPENDED: 'suspended',
} as const;

export type SeedUserStatus = (typeof USER_STATUS)[keyof typeof USER_STATUS];

// =============================================================================
// PASSWORD CONSTANTS
// =============================================================================

// DEFAULT_PASSWORD is imported from ../utils and re-exported above

/**
 * bcrypt hash of 'Demo123!' (12 rounds)
 * Use this for direct database seeding
 */
export const PASSWORD_HASH = '$2b$12$LQv3c1yqBWVHxkd0LHAkCOYz6TtxMQJqhN8/LewKyNi.YdZQJlZhy';

// =============================================================================
// USER DATA
// =============================================================================

export interface UserSeedData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  businessId: string;
  realmId: string;
  isActive: boolean;
  emailVerified: boolean;
  description?: string;
  portal: 'admin' | 'business' | 'consumer';
  /** User status for lifecycle tracking (Issue #469) */
  status?: SeedUserStatus;
  /** Date user was invited (for invited status) */
  invitedAt?: string;
  /** Date user was deactivated (for deactivated status) */
  deactivatedAt?: string;
  /** Reason for deactivation */
  deactivationReason?: string;
  /**
   * Signer role for term-sheet party resolution (Issue #1305 / #1531).
   * `credit_manager` users with `canCounterSignTermsheets=true` are the
   * canonical lender-side counter-signer cohort. Every business must have
   * at least one designated credit_manager so `resolveTermSheetSigners`
   * can auto-populate the SendForEsignPanel without manual picking.
   */
  signingRole?: 'credit_manager' | 'lender_signer';
  /** Counter-sign authority for term sheets — required for credit_manager. */
  canCounterSignTermsheets?: boolean;
}

export const USERS: Record<keyof typeof USER_IDS, UserSeedData> = {
  // Platform Users
  SUPER_ADMIN: {
    id: USER_IDS.SUPER_ADMIN,
    email: 'superadmin@platform.com',
    firstName: 'Super',
    lastName: 'Admin',
    role: USER_ROLES.SUPER_ADMIN,
    businessId: BUSINESS_IDS.PLATFORM_ADMIN,
    realmId: REALM_IDS.PLATFORM_DEFAULT,
    isActive: true,
    emailVerified: true,
    description: 'Full platform access - all tenants, realms, settings, billing',
    portal: 'admin',
  },
  PLATFORM_ADMIN: {
    id: USER_IDS.PLATFORM_ADMIN,
    email: 'admin@platform.com',
    firstName: 'Platform',
    lastName: 'Admin',
    role: USER_ROLES.PLATFORM_ADMIN,
    businessId: BUSINESS_IDS.PLATFORM_ADMIN,
    realmId: REALM_IDS.PLATFORM_DEFAULT,
    isActive: true,
    emailVerified: true,
    description: 'All tenants, realms, monitoring (no billing)',
    portal: 'admin',
  },

  // Fiops Capital Users
  RACHEL_MARTINEZ: {
    id: USER_IDS.RACHEL_MARTINEZ,
    email: 'rachel.martinez@opsmatrix.local',
    firstName: 'Rachel',
    lastName: 'Martinez',
    role: USER_ROLES.BUSINESS_ADMIN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'All loans, users, settings, reports, team management',
    portal: 'business',
    // Note: OPSMATRIX_CAPITAL also has the dedicated `demo-credit-manager`
    // user (provisioned by `scripts/seeders/termsheet-demo-signatures-seeder.ts`)
    // who carries the counter-signing role. Rachel stays a regular
    // business_admin so resolveCreditManagers returns the dedicated user
    // (cardinality:'one' would fail with two matches). Production tenants
    // get a credit_manager via the runtime `EnsureBusinessHasCreditManager`
    // bootstrap (Issue #1305 / #1531) — no static seed needed for them.
  },
  OPS_MANAGER: {
    id: USER_IDS.OPS_MANAGER,
    email: 'ops.manager@opsmatrix.local',
    firstName: 'Operations',
    lastName: 'Manager',
    role: USER_ROLES.OPS_MANAGER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Pipeline overview, all loans, assignments, reports',
    portal: 'business',
  },
  SARAH_CHEN: {
    id: USER_IDS.SARAH_CHEN,
    email: 'sarah.chen@opsmatrix.local',
    firstName: 'Sarah',
    lastName: 'Chen',
    role: USER_ROLES.UNDERWRITER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Loans in underwriting, credit analysis, conditions',
    portal: 'business',
  },
  MIKE_JOHNSON: {
    id: USER_IDS.MIKE_JOHNSON,
    email: 'mike.johnson@opsmatrix.local',
    firstName: 'Mike',
    lastName: 'Johnson',
    role: USER_ROLES.CLOSER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Loans ready to close, documents, funding queue',
    portal: 'business',
  },
  JAMES_WILSON: {
    id: USER_IDS.JAMES_WILSON,
    email: 'james.wilson@opsmatrix.local',
    firstName: 'James',
    lastName: 'Wilson',
    role: USER_ROLES.DRAW_INSPECTOR,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Construction loans, draw requests, inspections',
    portal: 'business',
  },
  EMILY_DAVIS: {
    id: USER_IDS.EMILY_DAVIS,
    email: 'emily.davis@opsmatrix.local',
    firstName: 'Emily',
    lastName: 'Davis',
    role: USER_ROLES.SERVICING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Funded loans, payments, payoff requests',
    portal: 'business',
  },
  // Additional Fiops Capital Users (PR #237 User/Employee Management)
  MIKE_CHEN: {
    id: USER_IDS.MIKE_CHEN,
    email: 'mike.chen@opsmatrix.local',
    firstName: 'Mike',
    lastName: 'Chen',
    role: USER_ROLES.CLOSER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Loan officer handling closings',
    portal: 'business',
  },
  SARAH_WILLIAMS: {
    id: USER_IDS.SARAH_WILLIAMS,
    email: 'sarah.williams@opsmatrix.local',
    firstName: 'Sarah',
    lastName: 'Williams',
    role: USER_ROLES.UNDERWRITER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Senior processor handling complex loans',
    portal: 'business',
  },
  DAVID_PARK: {
    id: USER_IDS.DAVID_PARK,
    email: 'david.park@opsmatrix.local',
    firstName: 'David',
    lastName: 'Park',
    role: USER_ROLES.UNDERWRITER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Senior underwriter',
    portal: 'business',
  },
  EMILY_NGUYEN: {
    id: USER_IDS.EMILY_NGUYEN,
    email: 'emily.nguyen@opsmatrix.local',
    firstName: 'Emily',
    lastName: 'Nguyen',
    role: USER_ROLES.CLOSER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Closer handling final documents',
    portal: 'business',
  },
  MARIA_RODRIGUEZ: {
    id: USER_IDS.MARIA_RODRIGUEZ,
    email: 'maria.rodriguez@opsmatrix.local',
    firstName: 'Maria',
    lastName: 'Rodriguez',
    role: USER_ROLES.OPS_MANAGER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Operations lead for sales team',
    portal: 'business',
  },
  JOHN_SMITH: {
    id: USER_IDS.JOHN_SMITH,
    email: 'john.smith@opsmatrix.local',
    firstName: 'John',
    lastName: 'Smith',
    role: USER_ROLES.CLOSER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Sales team member',
    portal: 'business',
  },
  ALEX_KUMAR: {
    id: USER_IDS.ALEX_KUMAR,
    email: 'alex.kumar@opsmatrix.local',
    firstName: 'Alex',
    lastName: 'Kumar',
    role: USER_ROLES.ANALYST,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Data analyst for underwriting team',
    portal: 'business',
  },

  // 1Sharpe Capital Users
  PORTFOLIO_MANAGER: {
    id: USER_IDS.PORTFOLIO_MANAGER,
    email: 'portfolio@1sharpe.local',
    firstName: 'Portfolio',
    lastName: 'Manager',
    role: USER_ROLES.PORTFOLIO_MANAGER,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'All 1Sharpe loans, portfolio analytics',
    portal: 'business',
  },
  ANALYST: {
    id: USER_IDS.ANALYST,
    email: 'analyst@1sharpe.local',
    firstName: 'Loan',
    lastName: 'Analyst',
    role: USER_ROLES.ANALYST,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Loan analysis, risk assessment',
    portal: 'business',
  },
  SHARPE_ADMIN: {
    id: USER_IDS.SHARPE_ADMIN,
    email: 'admin@1sharpe.local',
    firstName: 'Sharpe',
    lastName: 'Admin',
    role: USER_ROLES.BUSINESS_ADMIN,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: '1Sharpe Capital business admin',
    portal: 'business',
  },

  // Pacific Bridge Fund Users
  FUND_MANAGER: {
    id: USER_IDS.FUND_MANAGER,
    email: 'fund.manager@pacificbridge.local',
    firstName: 'Fund',
    lastName: 'Manager',
    role: USER_ROLES.FUND_MANAGER,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Fund operations, investments, reporting',
    portal: 'business',
  },
  PACIFIC_ADMIN: {
    id: USER_IDS.PACIFIC_ADMIN,
    email: 'admin@pacificbridge.local',
    firstName: 'Pacific',
    lastName: 'Admin',
    role: USER_ROLES.BUSINESS_ADMIN,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Pacific Bridge Fund business admin',
    portal: 'business',
  },

  // Gulf Coast Family Office Users
  PRINCIPAL: {
    id: USER_IDS.PRINCIPAL,
    email: 'principal@gulfcoastfo.local',
    firstName: 'Family',
    lastName: 'Principal',
    role: USER_ROLES.PRINCIPAL,
    businessId: BUSINESS_IDS.GULF_COAST_FO,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Family office investments, approvals',
    portal: 'business',
    status: USER_STATUS.ACTIVE,
  },
  // Issue #469: Invited/Deactivated/Status Variation Users
  INVITED_LOAN_OFFICER: {
    id: USER_IDS.INVITED_LOAN_OFFICER,
    email: 'jennifer.hayes@opsmatrix.local',
    firstName: 'Jennifer',
    lastName: 'Hayes',
    role: USER_ROLES.LOAN_OFFICER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: false,
    emailVerified: false,
    description: 'New loan officer - invitation pending',
    portal: 'business',
    status: USER_STATUS.INVITED,
    invitedAt: '2026-01-20T10:00:00Z',
  },
  INVITED_PROCESSOR: {
    id: USER_IDS.INVITED_PROCESSOR,
    email: 'kevin.brooks@opsmatrix.local',
    firstName: 'Kevin',
    lastName: 'Brooks',
    role: USER_ROLES.PROCESSOR,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: false,
    emailVerified: false,
    description: 'New processor - awaiting account setup',
    portal: 'business',
    status: USER_STATUS.INVITED,
    invitedAt: '2026-01-22T14:00:00Z',
  },
  DEACTIVATED_UNDERWRITER: {
    id: USER_IDS.DEACTIVATED_UNDERWRITER,
    email: 'thomas.anderson@opsmatrix.local',
    firstName: 'Thomas',
    lastName: 'Anderson',
    role: USER_ROLES.UNDERWRITER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: false,
    emailVerified: true,
    description: 'Former underwriter - left company',
    portal: 'business',
    status: USER_STATUS.DEACTIVATED,
    deactivatedAt: '2025-12-15T17:00:00Z',
    deactivationReason: 'Voluntary resignation',
  },
  SUSPENDED_ANALYST: {
    id: USER_IDS.SUSPENDED_ANALYST,
    email: 'lisa.chang@opsmatrix.local',
    firstName: 'Lisa',
    lastName: 'Chang',
    role: USER_ROLES.ANALYST,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: false,
    emailVerified: true,
    description: 'Analyst - temporarily suspended for policy review',
    portal: 'business',
    status: USER_STATUS.SUSPENDED,
    deactivatedAt: '2026-01-10T09:00:00Z',
    deactivationReason: 'Pending compliance review',
  },
  VIEWER_READONLY: {
    id: USER_IDS.VIEWER_READONLY,
    email: 'auditor@opsmatrix.local',
    firstName: 'External',
    lastName: 'Auditor',
    role: USER_ROLES.VIEWER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Read-only access for external auditing',
    portal: 'business',
    status: USER_STATUS.ACTIVE,
  },
  LISA_WANG: {
    id: USER_IDS.LISA_WANG,
    email: 'lisa.wang@opsmatrix.local',
    firstName: 'Lisa',
    lastName: 'Wang',
    role: USER_ROLES.PROCESSOR,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    isActive: true,
    emailVerified: true,
    description: 'Senior Loan Processor - Fiops Capital',
    portal: 'business',
    status: USER_STATUS.ACTIVE,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all users as an array for seeding
 */
export function getAllUsers(): UserSeedData[] {
  return Object.values(USERS);
}

/**
 * Get users for a specific business
 */
export function getUsersByBusiness(businessId: string): UserSeedData[] {
  return Object.values(USERS).filter((u) => u.businessId === businessId);
}

/**
 * Get users for a specific realm
 */
export function getUsersByRealm(realmId: string): UserSeedData[] {
  return Object.values(USERS).filter((u) => u.realmId === realmId);
}

/**
 * Get users by role
 */
export function getUsersByRole(role: string): UserSeedData[] {
  return Object.values(USERS).filter((u) => u.role === role);
}

/**
 * Get users for a specific portal
 */
export function getUsersByPortal(portal: 'admin' | 'business' | 'consumer'): UserSeedData[] {
  return Object.values(USERS).filter((u) => u.portal === portal);
}

/**
 * Get login credentials for a specific user
 */
export function getLoginCredentials(userId: keyof typeof USER_IDS): { email: string; password: string } {
  const user = USERS[userId];
  return {
    email: user.email,
    password: DEFAULT_PASSWORD,
  };
}

/**
 * Get users by status (Issue #469)
 */
export function getUsersByStatus(status: SeedUserStatus): UserSeedData[] {
  return Object.values(USERS).filter((u) => u.status === status);
}

/**
 * Get invited users (pending activation)
 */
export function getInvitedUsers(): UserSeedData[] {
  return Object.values(USERS).filter((u) => u.status === USER_STATUS.INVITED);
}

/**
 * Get deactivated users
 */
export function getDeactivatedUsers(): UserSeedData[] {
  return Object.values(USERS).filter((u) => u.status === USER_STATUS.DEACTIVATED);
}

/**
 * Get suspended users
 */
export function getSuspendedUsers(): UserSeedData[] {
  return Object.values(USERS).filter((u) => u.status === USER_STATUS.SUSPENDED);
}

/**
 * Get active users
 */
export function getActiveUsers(): UserSeedData[] {
  return Object.values(USERS).filter((u) => u.isActive && u.emailVerified);
}

// =============================================================================
// PASSWORD HISTORY IDs (Issue #267)
// =============================================================================

export const PASSWORD_HISTORY_IDS = {
  RACHEL_PW_CHANGE_1: seedId('pw-history-rachel-1', 1),
  RACHEL_PW_CHANGE_2: seedId('pw-history-rachel-2', 2),
  MIKE_CHEN_PW_CHANGE: seedId('pw-history-mike-1', 1),
  SUPER_ADMIN_PW_CHANGE: seedId('pw-history-superadmin-1', 1),
} as const;

export type PasswordHistoryId = (typeof PASSWORD_HISTORY_IDS)[keyof typeof PASSWORD_HISTORY_IDS];

// =============================================================================
// DEVICE IDs (Issue #267)
// =============================================================================

export const DEVICE_IDS = {
  RACHEL_IPHONE: seedId('device-rachel-iphone', 1),
  RACHEL_LAPTOP: seedId('device-rachel-laptop', 2),
  MIKE_CHEN_DESKTOP: seedId('device-mike-desktop', 1),
  SUPER_ADMIN_WORKSTATION: seedId('device-superadmin-ws', 1),
  SARAH_WILLIAMS_TABLET: seedId('device-sarah-tablet', 1),
} as const;

export type DeviceId = (typeof DEVICE_IDS)[keyof typeof DEVICE_IDS];

// =============================================================================
// PUSH TOKEN IDs (Push Notification E2E Testing)
// =============================================================================

export const PUSH_TOKEN_IDS = {
  RACHEL_IPHONE_PUSH: seedId('push-token-rachel-iphone', 1),
  SARAH_TABLET_PUSH: seedId('push-token-sarah-tablet', 1),
} as const;

export type PushTokenId = (typeof PUSH_TOKEN_IDS)[keyof typeof PUSH_TOKEN_IDS];

// =============================================================================
// USER SETTING IDs (Issue #267)
// =============================================================================

export const USER_SETTING_IDS = {
  RACHEL_NOTIFICATIONS: seedId('setting-rachel-notif', 1),
  RACHEL_APPEARANCE: seedId('setting-rachel-appear', 2),
  MIKE_CHEN_PRIVACY: seedId('setting-mike-privacy', 1),
  SUPER_ADMIN_GENERAL: seedId('setting-superadmin-gen', 1),
} as const;

export type UserSettingId = (typeof USER_SETTING_IDS)[keyof typeof USER_SETTING_IDS];

// =============================================================================
// USER PREFERENCES IDs (Issue #267)
// =============================================================================

export const USER_PREFERENCES_IDS = {
  RACHEL_PREFS: seedId('prefs-rachel', 1),
  MIKE_CHEN_PREFS: seedId('prefs-mike', 1),
  SUPER_ADMIN_PREFS: seedId('prefs-superadmin', 1),
  SARAH_WILLIAMS_PREFS: seedId('prefs-sarah', 1),
  DAVID_PARK_PREFS: seedId('prefs-david', 1),
} as const;

export type UserPreferencesId = (typeof USER_PREFERENCES_IDS)[keyof typeof USER_PREFERENCES_IDS];

// =============================================================================
// INVITE TEMPLATE IDs (Issue #267)
// =============================================================================

export const INVITE_TEMPLATE_IDS = {
  WELCOME_EMAIL_SYSTEM: seedId('invite-template-welcome-sys', 1),
  WELCOME_SMS_SYSTEM: seedId('invite-template-welcome-sms', 2),
  OPSMATRIX_CUSTOM_EMAIL: seedId('invite-template-ops-email', 1),
  SHARPE_CUSTOM_EMAIL: seedId('invite-template-sharpe-email', 1),
} as const;

export type InviteTemplateId = (typeof INVITE_TEMPLATE_IDS)[keyof typeof INVITE_TEMPLATE_IDS];

// =============================================================================
// INVITE IDs (Issue #267)
// =============================================================================

export const INVITE_IDS = {
  MARCUS_CONSUMER_INVITE: seedId('invite-marcus', 1),
  SARAH_CONSUMER_INVITE: seedId('invite-sarah-consumer', 1),
  PENDING_CONSUMER_INVITE: seedId('invite-pending', 1),
  EXPIRED_CONSUMER_INVITE: seedId('invite-expired', 1),
} as const;

export type InviteId = (typeof INVITE_IDS)[keyof typeof INVITE_IDS];

// =============================================================================
// PASSWORD HISTORY DATA TYPE
// =============================================================================

export interface PasswordHistorySeedData {
  id: string;
  userId: string;
  businessId: string;
  hashedPassword: string;
  algorithm: 'bcrypt' | 'argon2' | 'scrypt';
  createdAt: string;
}

// =============================================================================
// DEVICE DATA TYPE
// =============================================================================

export interface DeviceSeedData {
  id: string;
  userId: string;
  fingerprint: string;
  name: string;
  deviceType: 'mobile' | 'desktop' | 'tablet';
  browser?: string;
  os?: string;
  trusted: boolean;
  trustedAt?: string;
  trustExpiresAt?: string;
  firstSeenAt: string;
  lastSeenAt: string;
  lastIpAddress?: string;
  requiresMfa: boolean;
  /** FCM push notification token for this device */
  fcmToken?: string;
  /** Timestamp when the FCM token was last updated */
  fcmTokenUpdatedAt?: string;
}

// =============================================================================
// USER SETTING DATA TYPE
// =============================================================================

export interface UserSettingSeedData {
  id: string;
  userId: string;
  category: 'general' | 'notifications' | 'appearance' | 'privacy';
  key: string;
  value: string;
  valueType: 'string' | 'number' | 'boolean' | 'json';
  description?: string;
  isPublic: boolean;
  createdAt: string;
}

// =============================================================================
// USER PREFERENCES DATA TYPE
// =============================================================================

export interface UserPreferencesSeedData {
  id: string;
  userId: string;
  businessId: string;
  theme: 'light' | 'dark' | 'system';
  language: string;
  timezone: string;
  emailNotifications: boolean;
  pushNotifications: boolean;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

// =============================================================================
// INVITE TEMPLATE DATA TYPE
// =============================================================================

export interface InviteTemplateSeedData {
  id: string;
  businessId?: string; // null for system templates
  name: string;
  channel: 'EMAIL' | 'SMS';
  subject?: string;
  body: string;
  isDefault: boolean;
  createdAt: string;
}

// =============================================================================
// INVITE DATA TYPE
// =============================================================================

export interface InviteSeedData {
  id: string;
  businessId: string;
  consumerId: string;
  templateId: string;
  channel: 'EMAIL' | 'SMS';
  status: 'PENDING' | 'SENT' | 'DELIVERED' | 'OPENED' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED' | 'CANCELLED' | 'FAILED';
  customMessage?: string;
  token: string;
  createdBy?: string;
  sentAt?: string;
  deliveredAt?: string;
  openedAt?: string;
  acceptedAt?: string;
  declinedAt?: string;
  cancelledAt?: string;
  expiresAt: string;
  createdAt: string;
}

// =============================================================================
// PASSWORD HISTORY DATA
// =============================================================================

export const PASSWORD_HISTORIES: Record<keyof typeof PASSWORD_HISTORY_IDS, PasswordHistorySeedData> = {
  RACHEL_PW_CHANGE_1: {
    id: PASSWORD_HISTORY_IDS.RACHEL_PW_CHANGE_1,
    userId: USER_IDS.RACHEL_MARTINEZ,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    hashedPassword: '$2b$12$oldHash1XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX',
    algorithm: 'bcrypt',
    createdAt: '2025-06-01T00:00:00Z',
  },
  RACHEL_PW_CHANGE_2: {
    id: PASSWORD_HISTORY_IDS.RACHEL_PW_CHANGE_2,
    userId: USER_IDS.RACHEL_MARTINEZ,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    hashedPassword: '$2b$12$oldHash2YYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYY',
    algorithm: 'bcrypt',
    createdAt: '2025-09-15T00:00:00Z',
  },
  MIKE_CHEN_PW_CHANGE: {
    id: PASSWORD_HISTORY_IDS.MIKE_CHEN_PW_CHANGE,
    userId: USER_IDS.MIKE_CHEN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    hashedPassword: '$2b$12$oldHash3ZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZ',
    algorithm: 'bcrypt',
    createdAt: '2025-11-01T00:00:00Z',
  },
  SUPER_ADMIN_PW_CHANGE: {
    id: PASSWORD_HISTORY_IDS.SUPER_ADMIN_PW_CHANGE,
    userId: USER_IDS.SUPER_ADMIN,
    businessId: BUSINESS_IDS.PLATFORM_ADMIN,
    hashedPassword: '$2b$12$oldAdminAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA',
    algorithm: 'bcrypt',
    createdAt: '2025-01-01T00:00:00Z',
  },
};

// =============================================================================
// DEVICE DATA
// =============================================================================

export const DEVICES: Record<keyof typeof DEVICE_IDS, DeviceSeedData> = {
  RACHEL_IPHONE: {
    id: DEVICE_IDS.RACHEL_IPHONE,
    userId: USER_IDS.RACHEL_MARTINEZ,
    fingerprint: seedId('fp-rachel-iphone', 1),
    name: "Rachel's iPhone 15 Pro",
    deviceType: 'mobile',
    browser: 'Safari Mobile',
    os: 'iOS 18.0',
    trusted: true,
    trustedAt: '2025-06-15T10:00:00Z',
    trustExpiresAt: '2026-06-15T10:00:00Z',
    firstSeenAt: '2025-06-15T09:30:00Z',
    lastSeenAt: '2026-01-20T14:30:00Z',
    lastIpAddress: '192.168.1.100',
    requiresMfa: false,
    fcmToken: 'demo-fcm-token-rachel-iphone-001',
    fcmTokenUpdatedAt: '2026-01-15T10:00:00.000Z',
  },
  RACHEL_LAPTOP: {
    id: DEVICE_IDS.RACHEL_LAPTOP,
    userId: USER_IDS.RACHEL_MARTINEZ,
    fingerprint: seedId('fp-rachel-laptop', 2),
    name: "Rachel's MacBook Pro",
    deviceType: 'desktop',
    browser: 'Chrome',
    os: 'macOS Sonoma',
    trusted: true,
    trustedAt: '2025-06-01T08:00:00Z',
    trustExpiresAt: '2026-06-01T08:00:00Z',
    firstSeenAt: '2025-06-01T08:00:00Z',
    lastSeenAt: '2026-01-21T09:00:00Z',
    lastIpAddress: '10.0.0.50',
    requiresMfa: false,
  },
  MIKE_CHEN_DESKTOP: {
    id: DEVICE_IDS.MIKE_CHEN_DESKTOP,
    userId: USER_IDS.MIKE_CHEN,
    fingerprint: seedId('fp-mike-desktop', 1),
    name: "Mike's Work Desktop",
    deviceType: 'desktop',
    browser: 'Edge',
    os: 'Windows 11',
    trusted: true,
    trustedAt: '2025-08-01T09:00:00Z',
    trustExpiresAt: '2026-08-01T09:00:00Z',
    firstSeenAt: '2025-08-01T09:00:00Z',
    lastSeenAt: '2026-01-20T17:00:00Z',
    lastIpAddress: '10.0.0.75',
    requiresMfa: false,
  },
  SUPER_ADMIN_WORKSTATION: {
    id: DEVICE_IDS.SUPER_ADMIN_WORKSTATION,
    userId: USER_IDS.SUPER_ADMIN,
    fingerprint: seedId('fp-superadmin-ws', 1),
    name: 'Admin Secure Workstation',
    deviceType: 'desktop',
    browser: 'Firefox',
    os: 'Ubuntu 24.04',
    trusted: true,
    trustedAt: '2025-01-01T00:00:00Z',
    trustExpiresAt: '2026-01-01T00:00:00Z',
    firstSeenAt: '2025-01-01T00:00:00Z',
    lastSeenAt: '2026-01-22T08:00:00Z',
    lastIpAddress: '10.0.0.1',
    requiresMfa: true, // Admin always requires MFA
  },
  SARAH_WILLIAMS_TABLET: {
    id: DEVICE_IDS.SARAH_WILLIAMS_TABLET,
    userId: USER_IDS.SARAH_WILLIAMS,
    fingerprint: seedId('fp-sarah-tablet', 1),
    name: "Sarah's iPad Pro",
    deviceType: 'tablet',
    browser: 'Safari',
    os: 'iPadOS 18.0',
    trusted: false, // Not yet trusted
    firstSeenAt: '2026-01-15T10:00:00Z',
    lastSeenAt: '2026-01-15T10:00:00Z',
    lastIpAddress: '192.168.1.200',
    requiresMfa: true,
    fcmToken: 'demo-fcm-token-sarah-tablet-001',
    fcmTokenUpdatedAt: '2026-01-15T10:00:00.000Z',
  },
};

// =============================================================================
// USER SETTINGS DATA
// =============================================================================

export const USER_SETTINGS: Record<keyof typeof USER_SETTING_IDS, UserSettingSeedData> = {
  RACHEL_NOTIFICATIONS: {
    id: USER_SETTING_IDS.RACHEL_NOTIFICATIONS,
    userId: USER_IDS.RACHEL_MARTINEZ,
    category: 'notifications',
    key: 'email_digest_frequency',
    value: 'daily',
    valueType: 'string',
    description: 'How often to receive email digests',
    isPublic: false,
    createdAt: '2025-06-01T00:00:00Z',
  },
  RACHEL_APPEARANCE: {
    id: USER_SETTING_IDS.RACHEL_APPEARANCE,
    userId: USER_IDS.RACHEL_MARTINEZ,
    category: 'appearance',
    key: 'compact_mode',
    value: 'true',
    valueType: 'boolean',
    description: 'Use compact UI layout',
    isPublic: false,
    createdAt: '2025-06-01T00:00:00Z',
  },
  MIKE_CHEN_PRIVACY: {
    id: USER_SETTING_IDS.MIKE_CHEN_PRIVACY,
    userId: USER_IDS.MIKE_CHEN,
    category: 'privacy',
    key: 'show_online_status',
    value: 'false',
    valueType: 'boolean',
    description: 'Show online status to other users',
    isPublic: false,
    createdAt: '2025-08-01T00:00:00Z',
  },
  SUPER_ADMIN_GENERAL: {
    id: USER_SETTING_IDS.SUPER_ADMIN_GENERAL,
    userId: USER_IDS.SUPER_ADMIN,
    category: 'general',
    key: 'default_view',
    value: 'dashboard',
    valueType: 'string',
    description: 'Default view on login',
    isPublic: false,
    createdAt: '2025-01-01T00:00:00Z',
  },
};

// =============================================================================
// USER PREFERENCES DATA
// =============================================================================

export const USER_PREFERENCES: Record<keyof typeof USER_PREFERENCES_IDS, UserPreferencesSeedData> = {
  RACHEL_PREFS: {
    id: USER_PREFERENCES_IDS.RACHEL_PREFS,
    userId: USER_IDS.RACHEL_MARTINEZ,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    theme: 'dark',
    language: 'en',
    timezone: 'America/Chicago',
    emailNotifications: true,
    pushNotifications: true,
    metadata: { favoriteReports: ['pipeline', 'commissions'] },
    createdAt: '2025-06-01T00:00:00Z',
  },
  MIKE_CHEN_PREFS: {
    id: USER_PREFERENCES_IDS.MIKE_CHEN_PREFS,
    userId: USER_IDS.MIKE_CHEN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    theme: 'light',
    language: 'en',
    timezone: 'America/Chicago',
    emailNotifications: true,
    pushNotifications: false,
    createdAt: '2025-08-01T00:00:00Z',
  },
  SUPER_ADMIN_PREFS: {
    id: USER_PREFERENCES_IDS.SUPER_ADMIN_PREFS,
    userId: USER_IDS.SUPER_ADMIN,
    businessId: BUSINESS_IDS.PLATFORM_ADMIN,
    theme: 'system',
    language: 'en',
    timezone: 'UTC',
    emailNotifications: true,
    pushNotifications: true,
    metadata: { adminDashboardLayout: 'grid' },
    createdAt: '2025-01-01T00:00:00Z',
  },
  SARAH_WILLIAMS_PREFS: {
    id: USER_PREFERENCES_IDS.SARAH_WILLIAMS_PREFS,
    userId: USER_IDS.SARAH_WILLIAMS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    theme: 'light',
    language: 'en',
    timezone: 'America/New_York',
    emailNotifications: true,
    pushNotifications: true,
    createdAt: '2025-07-01T00:00:00Z',
  },
  DAVID_PARK_PREFS: {
    id: USER_PREFERENCES_IDS.DAVID_PARK_PREFS,
    userId: USER_IDS.DAVID_PARK,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    theme: 'dark',
    language: 'en',
    timezone: 'America/Chicago',
    emailNotifications: false, // Prefers minimal notifications
    pushNotifications: false,
    createdAt: '2025-09-01T00:00:00Z',
  },
};

// =============================================================================
// INVITE TEMPLATE DATA
// =============================================================================

export const INVITE_TEMPLATES: Record<keyof typeof INVITE_TEMPLATE_IDS, InviteTemplateSeedData> = {
  WELCOME_EMAIL_SYSTEM: {
    id: INVITE_TEMPLATE_IDS.WELCOME_EMAIL_SYSTEM,
    businessId: undefined, // System template
    name: 'Default Welcome Email',
    channel: 'EMAIL',
    subject: 'Welcome to {{businessName}} - Complete Your Registration',
    body: `Hi {{firstName}},

You've been invited to join {{businessName}} on Fiops.

Click the link below to complete your registration:
{{inviteLink}}

This link expires on {{expiresAt}}.

If you have any questions, please contact support.

Best regards,
The {{businessName}} Team`,
    isDefault: true,
    createdAt: '2025-01-01T00:00:00Z',
  },
  WELCOME_SMS_SYSTEM: {
    id: INVITE_TEMPLATE_IDS.WELCOME_SMS_SYSTEM,
    businessId: undefined, // System template
    name: 'Default Welcome SMS',
    channel: 'SMS',
    body: '{{businessName}}: You have been invited to join our platform. Complete registration: {{inviteLink}}',
    isDefault: true,
    createdAt: '2025-01-01T00:00:00Z',
  },
  OPSMATRIX_CUSTOM_EMAIL: {
    id: INVITE_TEMPLATE_IDS.OPSMATRIX_CUSTOM_EMAIL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Fiops Welcome Email',
    channel: 'EMAIL',
    subject: 'Your Loan Portal Access - Fiops Capital',
    body: `Dear {{firstName}},

Welcome to Fiops Capital! We're excited to have you on board.

Your dedicated loan officer has invited you to our secure borrower portal where you can:
- Track your loan status in real-time
- Upload documents securely
- Communicate with your loan team
- View closing timelines

Get started now: {{inviteLink}}

Questions? Reach out to your loan officer anytime.

Best,
Fiops Capital Team`,
    isDefault: false,
    createdAt: '2025-06-01T00:00:00Z',
  },
  SHARPE_CUSTOM_EMAIL: {
    id: INVITE_TEMPLATE_IDS.SHARPE_CUSTOM_EMAIL,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: '1Sharpe Welcome Email',
    channel: 'EMAIL',
    subject: 'Welcome to 1Sharpe Capital - Investor Portal Access',
    body: `Dear {{firstName}},

Thank you for choosing 1Sharpe Capital for your investment financing needs.

Access your investor portal here: {{inviteLink}}

We look forward to working with you.

1Sharpe Capital Team`,
    isDefault: false,
    createdAt: '2025-06-01T00:00:00Z',
  },
};

// =============================================================================
// INVITE DATA
// =============================================================================

import { CONSUMER_IDS } from './consumers';

export const INVITES: Record<keyof typeof INVITE_IDS, InviteSeedData> = {
  MARCUS_CONSUMER_INVITE: {
    id: INVITE_IDS.MARCUS_CONSUMER_INVITE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.MARCUS_THOMPSON,
    templateId: INVITE_TEMPLATE_IDS.OPSMATRIX_CUSTOM_EMAIL,
    channel: 'EMAIL',
    status: 'ACCEPTED',
    token: seedId('token-marcus-invite', 1),
    createdBy: USER_IDS.RACHEL_MARTINEZ,
    sentAt: '2025-06-15T10:00:00Z',
    deliveredAt: '2025-06-15T10:00:05Z',
    openedAt: '2025-06-15T11:30:00Z',
    acceptedAt: '2025-06-15T11:35:00Z',
    expiresAt: '2025-06-22T10:00:00Z',
    createdAt: '2025-06-15T09:55:00Z',
  },
  SARAH_CONSUMER_INVITE: {
    id: INVITE_IDS.SARAH_CONSUMER_INVITE,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.ALICIA_FERNANDEZ,
    templateId: INVITE_TEMPLATE_IDS.SHARPE_CUSTOM_EMAIL,
    channel: 'EMAIL',
    status: 'OPENED',
    token: seedId('token-sarah-invite', 1),
    createdBy: USER_IDS.SHARPE_ADMIN,
    sentAt: '2026-01-10T14:00:00Z',
    deliveredAt: '2026-01-10T14:00:08Z',
    openedAt: '2026-01-10T16:00:00Z',
    expiresAt: '2026-01-17T14:00:00Z',
    createdAt: '2026-01-10T13:55:00Z',
  },
  PENDING_CONSUMER_INVITE: {
    id: INVITE_IDS.PENDING_CONSUMER_INVITE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.ROBERT_CHEN,
    templateId: INVITE_TEMPLATE_IDS.WELCOME_EMAIL_SYSTEM,
    channel: 'EMAIL',
    status: 'PENDING',
    token: seedId('token-pending-invite', 1),
    createdBy: USER_IDS.MIKE_CHEN,
    expiresAt: '2026-01-29T00:00:00Z',
    createdAt: '2026-01-22T10:00:00Z',
  },
  EXPIRED_CONSUMER_INVITE: {
    id: INVITE_IDS.EXPIRED_CONSUMER_INVITE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.DAVID_WILLIAMS,
    templateId: INVITE_TEMPLATE_IDS.WELCOME_EMAIL_SYSTEM,
    channel: 'EMAIL',
    status: 'EXPIRED',
    token: seedId('token-expired-invite', 1),
    createdBy: USER_IDS.RACHEL_MARTINEZ,
    sentAt: '2025-11-01T10:00:00Z',
    deliveredAt: '2025-11-01T10:00:05Z',
    expiresAt: '2025-11-08T10:00:00Z', // Expired
    createdAt: '2025-11-01T09:55:00Z',
  },
};

// =============================================================================
// ACCOUNT SERVICE HELPER FUNCTIONS (Issue #267)
// =============================================================================

export function getAllPasswordHistories(): PasswordHistorySeedData[] {
  return Object.values(PASSWORD_HISTORIES);
}

export function getPasswordHistoriesByUser(userId: string): PasswordHistorySeedData[] {
  return Object.values(PASSWORD_HISTORIES).filter((h) => h.userId === userId);
}

export function getAllDevices(): DeviceSeedData[] {
  return Object.values(DEVICES);
}

export function getDevicesByUser(userId: string): DeviceSeedData[] {
  return Object.values(DEVICES).filter((d) => d.userId === userId);
}

export function getTrustedDevices(): DeviceSeedData[] {
  return Object.values(DEVICES).filter((d) => d.trusted);
}

export function getAllUserSettings(): UserSettingSeedData[] {
  return Object.values(USER_SETTINGS);
}

export function getUserSettingsByUser(userId: string): UserSettingSeedData[] {
  return Object.values(USER_SETTINGS).filter((s) => s.userId === userId);
}

export function getAllUserPreferences(): UserPreferencesSeedData[] {
  return Object.values(USER_PREFERENCES);
}

export function getUserPreferencesByBusiness(businessId: string): UserPreferencesSeedData[] {
  return Object.values(USER_PREFERENCES).filter((p) => p.businessId === businessId);
}

export function getAllInviteTemplates(): InviteTemplateSeedData[] {
  return Object.values(INVITE_TEMPLATES);
}

export function getSystemInviteTemplates(): InviteTemplateSeedData[] {
  return Object.values(INVITE_TEMPLATES).filter((t) => t.businessId === undefined);
}

export function getInviteTemplatesByBusiness(businessId: string): InviteTemplateSeedData[] {
  return Object.values(INVITE_TEMPLATES).filter((t) => t.businessId === businessId);
}

export function getAllInvites(): InviteSeedData[] {
  return Object.values(INVITES);
}

export function getInvitesByBusiness(businessId: string): InviteSeedData[] {
  return Object.values(INVITES).filter((i) => i.businessId === businessId);
}

export function getInvitesByStatus(status: InviteSeedData['status']): InviteSeedData[] {
  return Object.values(INVITES).filter((i) => i.status === status);
}
